import { usePlaygroundDispatch, usePlaygroundState } from "../usePlaygroundContext";
import {
  setPlaygroundPopoverOpen,
  setChatbotProps,
  setSessionId,
  setChatStatus,
} from "../playgroundSlice";
import {
  IconGripHorizontal,
  IconPlus,
  IconX,
  IconInfoCircle,
  IconArrowsDiagonalMinimize,
} from "@tabler/icons-react";
import { Stack } from "@altimateai/lego";
import { Button, toast, ToastAction, Typography } from "@altimateai/lego";
import { ChatEvent } from "@altimateai/ui-components/chatbotV2";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { Link } from "react-router-dom";
import ResettableDraggable from "./ResettableDraggable";
import PlaygroundChatbot, { PlaygroundChatbotRef } from "./PlaygroundChatbot";
import { useAppState } from "@/modules/app/useAppContext";
import {
  PLAYGROUND_SESSION_CREATED_EVENT,
  PlaygroundSessionCreatedDetail,
} from "../hooks/usePlaygroundRoute";
import PlaygroundStarterPrompts from "./PlaygroundStarterPrompts";
import { cn } from "@/lib/utils";

export interface PlaygroundPopoverRef {
  resetStatus: () => void;
}

const PlaygroundPopover = forwardRef<PlaygroundPopoverRef>((_, ref) => {
  const { previousPath } = useAppState();
  const dispatch = usePlaygroundDispatch();
  const {
    currentSession,
    chatbotProps,
    isPlaygroundPopoverOpen: isOpen,
    forceNewSession,
  } = usePlaygroundState();
  const chatRef = useRef<PlaygroundChatbotRef>(null);
  // ref to identify if any question is asked in this session to know if backend has stored this session id
  const isValidSession = useRef(false);
  // ref to track last processed forceNewSession timestamp
  const lastForceTimestamp = useRef<number | null>(null);
  // Gate for session_created events — only emissions we explicitly asked for
  // update our session id, ignoring echoes from prop syncs inside the chatbot.
  const expectingNewSessionRef = useRef(true);
  // Lets back-to-back handleNewSession calls collapse into one resetSession.
  const pendingResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sessionId = currentSession?.sessionId ?? null;
  const chatStatus = currentSession?.chatStatus ?? "idle";
  const context = currentSession?.context;

  console.log("global playground sessionId", sessionId, "user_view", context?.user_view);

  // If chat status is completed/error, and user clicks open, we need to reset this to idle state
  useEffect(() => {
    if (isOpen && chatRef.current && !["pending", "idle"].includes(chatStatus)) {
      chatRef.current.resetStatus();
    }
  }, [isOpen, chatStatus]);

  useImperativeHandle(ref, () => ({ resetStatus: () => chatRef.current?.resetStatus() }), []);

  const handleClose = () => {
    dispatch(setPlaygroundPopoverOpen(false));
  };

  const handleNewSession = useCallback(() => {
    if (isValidSession.current) {
      toast({
        icon: (
          <div
            className="al-rounded-full
  al-shadow-[0_0_0_2px_rgba(59,130,246,0.3),0_0_0_6px_rgba(59,130,246,0.15),0_0_0_10px_rgba(59,130,246,0.08)]"
          >
            <IconInfoCircle className="al-h-6 al-w-6 al-text-blue-500" />
          </div>
        ),
        title: "View in Studio",
        description: "Your conversation has been moved to Studio",
        action: (
          <ToastAction altText="View" asChild>
            <Button variant={"outline"} asChild>
              <Link to={`/studio/${sessionId}`}>View</Link>
            </Button>
          </ToastAction>
        ),
      });
    }
    // clear the bot props before resetting session to avoid infinite loop
    dispatch(setChatbotProps({ initialMessage: "", helloMessage: undefined }));

    if (pendingResetRef.current) clearTimeout(pendingResetRef.current);
    expectingNewSessionRef.current = true;
    pendingResetRef.current = setTimeout(() => {
      pendingResetRef.current = null;
      chatRef.current?.resetSession();
    }, 10);
  }, [sessionId, dispatch]);

  /**
   * reset session if request is not pending while switching route
   */
  useEffect(() => {
    if (isOpen && chatStatus === "pending") {
      return;
    }
    console.log("GlobalPlayground resetting status due to path update", previousPath, chatStatus);
    handleNewSession();
    // Disabling this explicitly to not have chatStatus which will cause infinite rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousPath]);

  /**
   * If new session needs to be created, ex when clicking analyse query button in query details
   */
  useEffect(() => {
    if (forceNewSession && forceNewSession !== lastForceTimestamp.current) {
      console.log(
        "GlobalPlayground forcing new session due to forceNewSession flag",
        forceNewSession
      );
      lastForceTimestamp.current = forceNewSession;
      handleNewSession();
    }
  }, [forceNewSession, handleNewSession]);

  const onChatEvent = useCallback(
    (event: ChatEvent) => {
      if (event.type === "session_created") {
        if (expectingNewSessionRef.current && sessionId !== event.sessionId) {
          expectingNewSessionRef.current = false;
          console.log("Playground popover, session created", event.sessionId);
          isValidSession.current = false;
          dispatch(setSessionId(event.sessionId));

          // Dispatch custom event for route context capture
          const customEvent = new CustomEvent<PlaygroundSessionCreatedDetail>(
            PLAYGROUND_SESSION_CREATED_EVENT,
            {
              detail: {
                sessionId: event.sessionId,
              },
            }
          );
          window.dispatchEvent(customEvent);
        }
      }
      if (event.type === "status_change" && event.sessionId === sessionId) {
        if (chatStatus !== event.status) {
          console.log(
            "Playground popover, status changes from %s to %s for session %s",
            chatStatus,
            event.status,
            event.sessionId
          );
          dispatch(
            setChatStatus({
              sessionId: event.sessionId,
              chatStatus: event.status,
            })
          );
          if (event.status === "pending") {
            isValidSession.current = true;
          }
        }
      }
    },
    [dispatch, sessionId, chatStatus]
  );

  const handleClear = () => {
    handleClose();
    handleNewSession();
  };

  return (
    <ResettableDraggable>
      <div
        className={`al-fixed al-bottom-20 al-right-6 al-w-[600px] al-bg-white al-rounded-lg al-shadow-xl al-border al-border-gray-200 al-overflow-hidden al-z-[50] ${isOpen ? "al-opacity-100 al-scale-100" : "al-opacity-0 al-scale-95 al-pointer-events-none"}`}
        style={{ height: "calc(100vh - 100px)" }}
      >
        <Stack direction="column" gap="none" className="al-h-full">
          {/* Drag Handle */}
          <Stack
            align="center"
            justify="center"
            className="drag-handle al-cursor-move hover:al-bg-gray-100 hover:al-bg-gray-200 al-transition-colors al-p"
          >
            <IconGripHorizontal size={16} className="al-text-gray-400" />
          </Stack>

          {/* Header */}
          <Stack
            direction="row"
            justify="between"
            align="center"
            gap="md"
            className="al-px-4 al-pb-2 al-border-b al-border-gray-200"
          >
            <Stack align="center">
              <Button
                variant="ghost"
                size="icon"
                title="New Session"
                onClick={handleNewSession}
                aria-label="New Session"
                className="[&_svg]:al-size-5 al-w-8 al-h-8"
              >
                <IconPlus size={18} />
              </Button>
              <Typography variant="section-heading">Studio</Typography>
              {isValidSession.current ? (
                <Button asChild variant={"link"} onClick={handleClose}>
                  <Link to={`/studio/${sessionId}`}>Expand in Studio</Link>
                </Button>
              ) : null}
            </Stack>
            <Stack>
              <Button
                variant="ghost"
                size="icon"
                title="Minimize window"
                onClick={handleClose}
                aria-label={"Minimize window"}
                className="[&_svg]:al-size-5 al-w-8 al-h-8"
              >
                <IconArrowsDiagonalMinimize />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title="Clear and close window"
                onClick={handleClear}
                aria-label="Close"
                className="[&_svg]:al-size-5 al-w-8 al-h-8"
              >
                <IconX size={18} />
              </Button>
            </Stack>
          </Stack>

          {/* Content */}
          <div className="al-flex-1 al-min-h-0 al-overflow-y-auto">
            <PlaygroundChatbot
              ref={chatRef}
              chatProviderProps={{
                context: context,
                currentSessionId: sessionId || undefined,
                onEvent: onChatEvent,
                chatboxStartPosition: "bottom",
                initialMessage: chatbotProps.initialMessage || undefined,
                submitInitialMessageOnMount: !!chatbotProps.initialMessage,
                helloMessage: chatbotProps.helloMessage || (
                  <PlaygroundStarterPrompts userView={context?.user_view} />
                ),
                helloMessageFooter: null,
              }}
            />
          </div>
          <Typography size="xs" className={cn("al-text-center al-py-2")}>
            AI can make mistakes. Review for accuracy.
          </Typography>
        </Stack>
      </div>
    </ResettableDraggable>
  );
});

PlaygroundPopover.displayName = "PlaygroundPopover";

export default PlaygroundPopover;
